package br.com.vvrefeicoes.portal.feature.rmp

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import br.com.vvrefeicoes.portal.core.di.LocalAppGraph
import br.com.vvrefeicoes.portal.domain.model.RmpLinkField
import br.com.vvrefeicoes.portal.domain.model.RmpPortalOption
import br.com.vvrefeicoes.portal.domain.model.RmpSyncCell
import br.com.vvrefeicoes.portal.domain.model.RmpSyncPage
import br.com.vvrefeicoes.portal.domain.model.RmpSyncRecord
import br.com.vvrefeicoes.portal.domain.model.RmpSyncSituation
import br.com.vvrefeicoes.portal.domain.model.RmpSyncStatusFilter
import br.com.vvrefeicoes.portal.domain.repository.RmpRepository
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.async
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch

private const val SEARCH_DEBOUNCE_MS = 400L

/**
 * Aba Sync: os RMPs (filtrados por status) com as colunas da RMP, ajustados ao portal linha a linha. Cada ajuste
 * vale só para o RMP em que foi feito; depois de salvar, a página é recarregada para refletir a pendência da linha.
 */
class RmpSyncController(
    private val repository: RmpRepository,
    private val scope: CoroutineScope,
) {
    var loading by mutableStateOf(true)
        private set

    var errorMessage by mutableStateOf<String?>(null)
        private set

    /** Falha ao salvar um ajuste. */
    var feedback by mutableStateOf<String?>(null)
        private set

    var syncPage by mutableStateOf<RmpSyncPage?>(null)
        private set

    var statusFilter by mutableStateOf(RmpSyncStatusFilter.FINALIZADOS)
        private set

    var typeFilter by mutableStateOf<String?>(null)
        private set

    var situation by mutableStateOf(RmpSyncSituation.PENDENTES)
        private set

    var query by mutableStateOf("")
        private set

    var page by mutableStateOf(0)
        private set

    var portalUnits by mutableStateOf<List<RmpPortalOption>>(emptyList())
        private set

    var portalUsers by mutableStateOf<List<RmpPortalOption>>(emptyList())
        private set

    /** Células (RMP + coluna) com gravação em andamento, para travar o seletor enquanto salva. */
    var saving by mutableStateOf<Set<String>>(emptySet())
        private set

    /** Linha da aba Ajustados em edição: os seletores mudam só o rascunho, gravado no "Salvar". */
    var editingId by mutableStateOf<String?>(null)
        private set

    /** Colunas alteradas no rascunho da linha em edição (coluna → unidade/usuário; null remove o ajuste). */
    var editDraft by mutableStateOf<Map<RmpLinkField, String?>>(emptyMap())
        private set

    val pageSize = 20

    val pageCount: Int
        get() = maxOf(1, syncPage?.totalPages ?: 1)

    private var loadJob: Job? = null
    private var initialized = false

    fun init() {
        if (initialized) return
        initialized = true
        scope.launch {
            try {
                coroutineScope {
                    val units = async { repository.loadPortalUnits() }
                    val users = async { repository.loadPortalUsers() }
                    portalUnits = units.await()
                    portalUsers = users.await()
                }
            } catch (exception: CancellationException) {
                throw exception
            } catch (throwable: Throwable) {
                errorMessage = throwable.message ?: "Não foi possível carregar as unidades e os usuários do portal."
            }
        }
        requestPage()
    }

    fun retry() {
        errorMessage = null
        initialized = false
        init()
    }

    fun updateStatusFilter(value: RmpSyncStatusFilter) {
        if (value == statusFilter) return
        statusFilter = value
        page = 0
        requestPage()
    }

    fun updateTypeFilter(value: String?) {
        if (value == typeFilter) return
        typeFilter = value
        page = 0
        requestPage()
    }

    fun updateSituation(value: RmpSyncSituation) {
        if (value == situation) return
        situation = value
        page = 0
        cancelEdit()
        requestPage()
    }

    fun updateQuery(value: String) {
        query = value
        page = 0
        requestPage(debounce = true)
    }

    fun goToPage(value: Int) {
        val target = value.coerceIn(0, pageCount - 1)
        if (target == page) return
        page = target
        cancelEdit()
        requestPage()
    }

    fun dismissFeedback() {
        feedback = null
    }

    fun isSaving(
        rmpRecordId: String,
        field: RmpLinkField,
    ): Boolean = key(rmpRecordId, field) in saving

    /** Ajusta uma coluna só deste RMP; `targetId` null remove o ajuste. */
    fun link(
        rmpRecordId: String,
        field: RmpLinkField,
        targetId: String?,
    ) {
        val key = key(rmpRecordId, field)
        if (key in saving) return
        saving = saving + key
        scope.launch {
            try {
                repository.updateLink(rmpRecordId, field, targetId)
                requestPage()
            } catch (exception: CancellationException) {
                throw exception
            } catch (throwable: Throwable) {
                feedback = throwable.message ?: "Não foi possível salvar o ajuste."
            } finally {
                saving = saving - key
            }
        }
    }

    fun isConfirming(rmpRecordId: String): Boolean = "$rmpRecordId:ROW" in saving

    /** "Confirmar linha": grava as sugestões pré-selecionadas das colunas ainda sem ajuste deste RMP. */
    fun confirmRow(record: RmpSyncRecord) {
        val links = suggestionsToConfirm(record)
        val key = "${record.id}:ROW"
        if (links.isEmpty() || key in saving) return
        saving = saving + key
        scope.launch {
            try {
                repository.updateLinks(record.id, links)
                requestPage()
            } catch (exception: CancellationException) {
                throw exception
            } catch (throwable: Throwable) {
                feedback = throwable.message ?: "Não foi possível confirmar a linha."
            } finally {
                saving = saving - key
            }
        }
    }

    fun isEditing(rmpRecordId: String): Boolean = editingId == rmpRecordId

    /** "Editar": abre a linha já ajustada para correção; outra linha em edição perde o rascunho. */
    fun startEdit(rmpRecordId: String) {
        editingId = rmpRecordId
        editDraft = emptyMap()
    }

    fun cancelEdit() {
        editingId = null
        editDraft = emptyMap()
    }

    fun updateDraft(
        record: RmpSyncRecord,
        field: RmpLinkField,
        targetId: String?,
    ) {
        if (editingId != record.id) return
        val saved = record.cells().first { it.field == field }.targetId
        editDraft = if (targetId == saved) editDraft - field else editDraft + (field to targetId)
    }

    /** "Salvar": grava só as colunas alteradas no rascunho; remover um ajuste devolve o RMP para Pendentes. */
    fun saveEdit(record: RmpSyncRecord) {
        val changes = editDraft
        val key = "${record.id}:ROW"
        if (editingId != record.id || key in saving) return
        if (changes.isEmpty()) {
            cancelEdit()
            return
        }
        saving = saving + key
        scope.launch {
            try {
                changes.filterValues { it == null }.keys.forEach { field -> repository.updateLink(record.id, field, null) }
                val links = changes.mapNotNull { (field, targetId) -> targetId?.let { field to it } }.toMap()
                if (links.isNotEmpty()) repository.updateLinks(record.id, links)
                cancelEdit()
                requestPage()
            } catch (exception: CancellationException) {
                throw exception
            } catch (throwable: Throwable) {
                feedback = throwable.message ?: "Não foi possível salvar a edição."
            } finally {
                saving = saving - key
            }
        }
    }

    private fun key(
        rmpRecordId: String,
        field: RmpLinkField,
    ) = "$rmpRecordId:${field.name}"

    private fun requestPage(debounce: Boolean = false) {
        loadJob?.cancel()
        loadJob =
            scope.launch {
                if (debounce) delay(SEARCH_DEBOUNCE_MS)
                loadPage()
            }
    }

    private suspend fun loadPage() {
        loading = syncPage == null
        errorMessage = null
        try {
            val result =
                repository.loadSyncRecords(
                    page = page,
                    size = pageSize,
                    type = typeFilter,
                    status = statusFilter,
                    situation = situation,
                    query = query,
                )
            syncPage = result
            if (page > result.totalPages - 1) page = maxOf(0, result.totalPages - 1)
        } catch (exception: CancellationException) {
            throw exception
        } catch (throwable: Throwable) {
            errorMessage = throwable.message ?: "Não foi possível carregar os RMPs."
        } finally {
            loading = false
        }
    }
}

/**
 * Colunas pendentes do RMP que têm sugestão (a opção pré-selecionada na tela). A Diretora Destino só conta em
 * transferência, como na listagem da RMP.
 */
internal fun suggestionsToConfirm(record: RmpSyncRecord): Map<RmpLinkField, String> =
    record
        .cells()
        .filter { cell -> cell.pending && cell.suggestedId != null }
        .filter { cell -> cell.field != RmpLinkField.DIRETORA_DESTINO || record.type == "transferencia" }
        .associate { cell -> cell.field to cell.suggestedId!! }

internal fun RmpSyncRecord.cells(): List<RmpSyncCell> = listOf(unit, requester, destinationDirector, director, generalDirector, hr)

@Composable
fun rememberRmpSyncController(): RmpSyncController {
    val appGraph = LocalAppGraph.current
    val scope = rememberCoroutineScope()
    return remember { RmpSyncController(appGraph.rmpRepository, scope) }
}
